import Modal from '@ui/modal/modal';
import Button from '@ui/buttons/button/button';
import Text from '@ui/text/text/text';
import Container from '@ui/container/container';
import { Dispatch, useEffect, useState } from 'react';
import ManualInviteRow from '../manual-add-user-row';
import { TButtonAndLinkDesigns } from '@styles/design-system/local-components/local-components.types';
import Icon from '@ui/icons/icon';
import { ICONS } from '@ui/icons/types';
import { allListMembersValid, getDefaultPasscodeList } from '../../../utils';
import { PasscodeList } from '@/types/registration.types';
import { useAppSelector } from '@/store/hooks';
import { getWorkingProjectUuid } from '@/store/slices/project/selectors';
import { WorkingPasscodeList } from '@/types/passcode-list.types';
import ConfirmUploadRegistrantsModal from './confirm-upload-registrants-modal';
import getThemeColorCSSVariable from '@/utils/colors/get-theme-color-css-variable';
import { EColorVariablesV2 } from '@/types/theme-packs.types';
import getThemeColorContrastCSSVariable from '@/utils/colors/get-theme-color-contrast-css-variable';

type ManualInviteRowData = {
  email: string;
  firstName: string;
  lastName: string;
};

type AddRegistrantsModalProps = {
  open: boolean;
  setOpen: Dispatch<React.SetStateAction<boolean>>;
  onSave: (workingPasscodeList: WorkingPasscodeList) => Promise<void>;
  saving: boolean;
  passcodeList?: PasscodeList;
  onSaveComplete?: () => void;
};

const AddRegistrantsModal = ({
  open,
  setOpen,
  onSave,
  saving,
  passcodeList,
  onSaveComplete,
}: AddRegistrantsModalProps) => {
  const projectUuid = useAppSelector(getWorkingProjectUuid);

  const [rows, setRows] = useState<ManualInviteRowData[]>([{ email: '', firstName: '', lastName: '' }]);
  const [fileName, setFileName] = useState<string | null>(null);
  const [workingPasscodeList, setWorkingPasscodeList] = useState<WorkingPasscodeList>(() =>
    getDefaultPasscodeList(projectUuid || '', true)
  );
  const [testEmailAddressesForSend, setTestEmailAddressesForSend] = useState<string[]>([]);
  const [sendTestEmailModalOpen, setSendTestEmailModalOpen] = useState(false);

  useEffect(() => {
    const manuallyAddedRows = rows
      .filter((row) => row.email.trim())
      .map((row) => ({
        email: row.email,
        first_name: row.firstName,
        last_name: row.lastName,
        passcode: '',
      }));

    setWorkingPasscodeList((list) => ({
      ...list,
      manually_added_users: manuallyAddedRows,
    }));
  }, [rows]);

  const handleClose = () => {
    setOpen(false);
    setRows([{ email: '', firstName: '', lastName: '' }]);
    setFileName(null);
    setSendTestEmailModalOpen(false);
    setTestEmailAddressesForSend([]);
  };

  const handleAddRow = () => {
    setRows([...rows, { email: '', firstName: '', lastName: '' }]);
  };

  const handleRowChange = (index: number, field: keyof ManualInviteRowData, value: string) => {
    const updatedRows = [...rows];
    updatedRows[index] = { ...updatedRows[index], [field]: value };
    setRows(updatedRows);
  };

  const handleDeleteRow = (index: number) => {
    setRows(rows.filter((_, i) => i !== index));
  };

  const handleSave = async () => {
    const listToSave = {
      ...workingPasscodeList,
      fileName: fileName || undefined,
      ...(passcodeList?.type != null && { type: passcodeList.type }),
    };

    // merge test email addresses if any
    if (testEmailAddressesForSend.length > 0) {
      const testUsers = testEmailAddressesForSend.map((email) => ({
        email,
        first_name: 'Test',
        last_name: 'Email',
        passcode: '',
      }));
      listToSave.manually_added_users = [...(listToSave.manually_added_users || []), ...testUsers];
    }

    await onSave(listToSave);

    onSaveComplete?.();
    handleClose();
  };

  const isSaveDisabled = saving || !allListMembersValid(workingPasscodeList);

  return (
    <>
      <Modal
        open={open}
        onRequestClose={handleClose}
        customSize={{
          width: '720px',
          height: 'auto',
          maxHeight: '800px',
          maxWidth: 'calc(100% - 32px)',
        }}
        contentWrapperClassName="padding-24 overflow-y-auto flex-1 flex-column"
        backgroundColor="var(--gray-900)"
        RenderHeaderContent={() => (
          <Container className="flex-column gap-8 padding-b-16" borderRadius="16px">
            <Text className="H4">Add new registrants</Text>
            <Text className="P1" color="var(--gray-500)">
              Create registrations for targeted invitees. Project must be published first.
            </Text>
          </Container>
        )}
        RenderFooterContent={() => (
          <Container background="var(--gray-900)" className="flex fd-row jc-flex-end ai-flex-end gap-16">
            <Button
              design={TButtonAndLinkDesigns.LocalBttnSecHalfTransp}
              onClick={handleClose}
              className="L1 padding-v-16 padding-h-24"
            >
              <Text className="L1">Cancel</Text>
            </Button>
            <Button
              onClick={() => {
                if (!passcodeList) {
                  setSendTestEmailModalOpen(true);
                } else {
                  handleSave();
                }
              }}
              className="padding-v-16 padding-h-24"
              disabled={isSaveDisabled}
              disabledOpacity={0.6}
              backgroundColor={getThemeColorCSSVariable(EColorVariablesV2.primary)}
            >
              <Text
                className="L1"
                color={getThemeColorContrastCSSVariable(EColorVariablesV2.buttonText, EColorVariablesV2.primary)}
              >
                Save
              </Text>
            </Button>
          </Container>
        )}
      >
        <Container className="flex-column gap-4 padding-h-16 padding-v-12">
          <Container className="flex ai-center gap-12">
            <Container className="flex-2">
              <Text className="L2 padding-l-12" color="var(--gray-500)">
                Email*
              </Text>
            </Container>
            <Container className="flex-1">
              <Text className="L2 padding-l-12" color="var(--gray-500)">
                First name
              </Text>
            </Container>
            <Container className="flex-1">
              <Text className="L2 padding-l-12" color="var(--gray-500)">
                Last name
              </Text>
            </Container>
            <div style={{ width: '12px' }}></div> {/* needed for spacing */}
          </Container>
          <Container className="flex-column gap-12" style={{ overflowY: 'auto' }} maxHeight="132px">
            {rows.map((row, index) => (
              <ManualInviteRow
                key={index}
                email={row.email}
                firstName={row.firstName}
                lastName={row.lastName}
                onEmailChange={(value) => handleRowChange(index, 'email', value)}
                onFirstNameChange={(value) => handleRowChange(index, 'firstName', value)}
                onLastNameChange={(value) => handleRowChange(index, 'lastName', value)}
                onDelete={rows.length > 1 ? () => handleDeleteRow(index) : undefined}
              />
            ))}
          </Container>
          <Button
            design={TButtonAndLinkDesigns.LocalBttnSecHalfTransp}
            width="fit-content"
            onClick={handleAddRow}
            className="flex ai-center gap-6 margin-t-6"
            backgroundColor="var(--gray-700)"
          >
            <Icon name={ICONS.ADD} size={12} />
            <Text className="L1">Add</Text>
          </Button>
        </Container>
      </Modal>
      <ConfirmUploadRegistrantsModal
        open={sendTestEmailModalOpen}
        setOpen={setSendTestEmailModalOpen}
        setTestEmailAddressesForSend={setTestEmailAddressesForSend}
        sendEmails={handleSave}
        isSaving={saving}
        saveDisabled={isSaveDisabled}
      />
    </>
  );
};

export default AddRegistrantsModal;
